import { YokaiImage } from '../types/yokai';
import { API_SETTINGS } from '../constants/config';
import { logger } from '../utils/logger';
import { handleFalApiError } from '../utils/errorHandler';
import { validateImageData } from '../utils/validation';

export interface GenerateImageRequest {
  prompt: string;
  num_images?: number;
  image_size?: {
    width: number;
    height: number;
  } | string;
}

export interface FalImageResponse {
  images: Array<{
    url: string;
    width: number;
    height: number;
    content_type: string;
  }>;
  seed: number;
  has_nsfw_concepts: boolean[];
  prompt: string;
}

/**
 * サーバーサイドプロキシ経由でfal.ai APIを呼び出し、妖怪画像を生成
 */
export const generateYokaiImages = async (prompt: string): Promise<YokaiImage[]> => {
  logger.apiRequest('FAL-AI', 'flux-pro/v1.1', { prompt: prompt.substring(0, 50) + '...' });

  try {
    // サーバーサイドプロキシ経由でAPIを呼び出す
    const response = await fetch('/api/fal', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        prompt: prompt,
        enable_safety_checker: true,
        guidance_scale: API_SETTINGS.FAL.GUIDANCE_SCALE,
        num_images: API_SETTINGS.FAL.NUM_IMAGES,
        image_size: {
          width: API_SETTINGS.FAL.IMAGE_SIZE.WIDTH,
          height: API_SETTINGS.FAL.IMAGE_SIZE.HEIGHT,
        },
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error((errorData as any).error || `APIエラー (${response.status})`);
    }

    const data = await response.json() as FalImageResponse;

    if (!data) {
      throw new Error('APIから有効なレスポンスが返されませんでした');
    }

    logger.debug('FAL_API', 'APIレスポンス詳細', {
      images: data.images?.length || 0,
      seed: data.seed,
      prompt: data.prompt?.substring(0, 50) + '...',
      has_nsfw_concepts: data.has_nsfw_concepts
    });

    if (!data.images || data.images.length === 0) {
      throw new Error('画像が生成されませんでした');
    }

    // 各画像データを検証して妖怪画像形式に変換
    const yokaiImages: YokaiImage[] = data.images.map((image, index) => {
      try {
        validateImageData(image);
      } catch (validationError) {
        logger.warn('FAL_API', `画像${index + 1}のデータ検証警告`, validationError);
      }

      return {
        url: image.url,
        width: image.width,
        height: image.height,
        content_type: image.content_type
      };
    });

    logger.info('FAL_API', `${yokaiImages.length}枚の画像を生成しました`);
    return yokaiImages;

  } catch (error) {
    logger.error('FAL_API', '画像生成最終エラー', error);
    throw handleFalApiError(error);
  }
};
